<template>
  <div>
    <div class="d-flex flex-column flex-sm-row align-sm-center justify-space-between mb-4">
      <div>
        <h3 class="text-subtitle-1 primary--text mb-1">RESPONSÁVEIS</h3>
        <p class="mb-0 text-body-2 grey--text text--darken-1">
          Informe um ou mais responsaveis vinculados a este {{ getTipoContato }}.
        </p>
      </div>
      <v-btn color="primary" small class="mt-3 mt-sm-0" @click="adicionarResponsavel">
        <v-icon left>mdi-playlist-plus</v-icon>
        Adicionar responsável
      </v-btn>
    </div>

    <v-text-field
      v-if="obrigatorio"
      class="responsavel-validator"
      :value="contatos.length"
      :rules="[validarResponsaveisObrigatorio]"
      readonly
      tabindex="-1"
      aria-hidden="true"
    />

    <v-alert
      v-if="obrigatorio && (!contatos || contatos.length === 0)"
      type="info"
      dense
      outlined
      class="mb-4"
    >
      Informe pelo menos um responsável para continuar.
    </v-alert>

    <v-card
      v-for="(contato, index) in contatos"
      :key="getContatoKey(contato, index)"
      outlined
      class="mb-4 responsavel-card"
    >
      <div class="responsavel-card__titulo d-flex flex-column flex-sm-row align-sm-center justify-space-between">
        <div class="font-weight-medium primary--text">
          Responsável {{ index + 1 }}
        </div>
        <v-btn icon small color="error" @click="removerResponsavel(index)">
          <v-icon>mdi-delete</v-icon>
        </v-btn>
      </div>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              label="* Nome"
              dense
              :rules="[
                validators.string.required,
                v => validators.string.greaterThanOrEquals(v, 1),
                v => validators.string.lessThanOrEquals(v, 100),
              ]"
              v-model.trim="contato.nome"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              label="* Cargo"
              dense
              :rules="[
                validators.string.required,
                v => validators.string.greaterThanOrEquals(v, 1),
                v => validators.string.lessThanOrEquals(v, 100),
              ]"
              v-model.trim="contato.cargo"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              label="* Celular"
              dense
              v-mask="'(##) #####-####'"
              :rules="[
                validators.string.required,
              ]"
              v-model.trim="contato.celular"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-if="empresaEstrangeira"
              :label="labelCpfResponsavel"
              :key="'cpf-estrangeira'"
              dense
              maxLength="20"
              placeholder="Registro do responsável"
              :rules="rulesCpfResponsavel"
              v-model.trim="contato.cpf"
            />
            <v-text-field
              v-else
              :label="labelCpfResponsavel"
              :key="'cpf-nacional'"
              dense
              placeholder="000.000.000-00"
              :rules="rulesCpfResponsavel"
              v-mask="'###.###.###-##'"
              v-model.trim="contato.cpf"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              label="Telefone"
              dense
              v-mask="'(##) ####-####'"
              v-model.trim="contato.telefone"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              label="Data Nascimento"
              dense
              type="date"
              v-model="contato.dataNascimento"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              label="* Email"
              dense
              :rules="[
                validators.string.required,
                validators.string.email,
                v => validators.string.lessThanOrEquals(v, 50),
              ]"
              v-model.trim="contato.email"
            />
          </v-col>
          <v-col cols="12" md="6">
            <span v-if="euEstouEditandoMeuCadastro(contato)">
              Para alterar sua senha, faca pelo menu: Meus Dados -> Alterar Senha
            </span>
            <v-text-field
              v-else
              label="* Senha"
              dense
              type="password"
              :rules="[
                validators.string.required,
                v => validators.string.greaterThanOrEquals(v, 1),
                v => validators.string.lessThanOrEquals(v, 100),
              ]"
              v-model="contato.senha"
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import {mapGetters} from "vuex";

export default {
  name: "ResponsaveisInlineForm",
  props: {
    contatos: {
      type: Array,
      default: () => []
    },
    tipoContato: {
      type: String,
      default: ""
    },
    empresaEstrangeira: {
      type: Boolean,
      default: false
    },
    obrigatorio: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    ...mapGetters('auth', ['user']),
    validators() {
      return this.$validators;
    },
    getTipoContato() {
      return this.tipoContato === 'COM' ? 'Comprador' : 'Fornecedor';
    },
    temResponsavel() {
      return Array.isArray(this.contatos) && this.contatos.length > 0;
    },
    labelCpfResponsavel() {
      return this.empresaEstrangeira ? 'Registro do responsável' : 'CPF';
    },
    rulesCpfResponsavel() {
      const rules = [];
      if (this.empresaEstrangeira) {
        rules.push(v => this.validators.string.greaterThanOrEquals(v, 1));
        rules.push(v => this.validators.string.lessThanOrEquals(v, 50));
      }
      return rules;
    }
  },
  methods: {
    validarResponsaveisObrigatorio() {
      return !this.obrigatorio || this.temResponsavel
        ? true
        : 'Informe pelo menos um responsável.';
    },
    criarContatoVazio() {
      return {
        id: 0,
        nome: "",
        cargo: "",
        cpf: "",
        telefone: "",
        celular: "",
        email: "",
        dataNascimento: "",
        senha: "",
      };
    },
    adicionarResponsavel() {
      this.contatos.push(this.criarContatoVazio());
    },
    removerResponsavel(index) {
      this.contatos.splice(index, 1);
    },
    euEstouEditandoMeuCadastro(contato) {
      return contato.id === this.user?.idResponsavel;
    },
    getContatoKey(contato, index) {
      return contato.id || `novo-${index}`;
    }
  }
}
</script>

<style scoped>
.responsavel-card {
  border-color: #dce9f4;
  overflow: hidden;
}

.responsavel-card__titulo {
  background: rgba(22, 122, 188, 0.08);
  border-bottom: 1px solid #dce9f4;
  padding: 10px 16px;
}

.responsavel-validator {
  position: absolute;
  opacity: 0;
  pointer-events: none;
  height: 0;
  width: 0;
  overflow: hidden;
}
</style>